import React, { useEffect, useState, useCallback } from "react";
import { ChevronsUp, ChevronsDown, Square, Loader2, RefreshCw, Wifi, WifiOff, AlertTriangle } from "lucide-react";
import axios from "axios";
import { toast } from "../../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${BACKEND_URL}/api` });
client.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("admin_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Live Tuya cover controller — lists registered covers and lets the admin
// open/stop/close them or drive them to a specific percentage. Auto-polls
// every 8 s while mounted so the position reflects reality.
export default function TapparellePanel() {
  const [covers, setCovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // { device_id: true } while a command is in flight

  const load = useCallback(async () => {
    try {
      const { data } = await client.get("/tuya/covers");
      setCovers(data.covers || []);
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh on mount + every 8s so position/state feel live.
  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const command = async (deviceId, action) => {
    setBusy((b) => ({ ...b, [deviceId]: true }));
    try {
      await client.post(`/tuya/covers/${deviceId}/control`, { action });
      // Refresh after a short delay so we catch the transition.
      setTimeout(load, 1200);
    } catch (e) {
      toast({ title: "Comando fallito", description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy((b) => ({ ...b, [deviceId]: false }));
    }
  };

  const setPosition = async (deviceId, position) => {
    setBusy((b) => ({ ...b, [deviceId]: true }));
    try {
      await client.post(`/tuya/covers/${deviceId}/position`, { position });
      setTimeout(load, 1500);
    } catch (e) {
      toast({ title: "Comando fallito", description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy((b) => ({ ...b, [deviceId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl bg-white border border-[#e8dcc8] p-8 flex justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" />
      </div>
    );
  }

  if (covers.length === 0) return null; // hide entirely if none registered yet

  return (
    <div className="rounded-2xl bg-white border border-[#e8dcc8] p-4 space-y-3" data-testid="tapparelle-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-lg font-bold text-[#2b1410]">Tapparelle · Controllo diretto</h3>
        <button
          onClick={load}
          className="inline-flex items-center gap-1 text-[11px] text-[#7c5a4e] hover:text-[#2b1410]"
          data-testid="tapparelle-refresh"
        >
          <RefreshCw className="h-3 w-3" /> Aggiorna
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {covers.map((c) => (
          <CoverCard
            key={c.device_id}
            cover={c}
            busy={!!busy[c.device_id]}
            onCommand={(a) => command(c.device_id, a)}
            onPosition={(p) => setPosition(c.device_id, p)}
          />
        ))}
      </div>
    </div>
  );
}

function CoverCard({ cover, busy, onCommand, onPosition }) {
  const pos = typeof cover.percent_state === "number" ? cover.percent_state : (cover.percent_control || 0);
  // Physical convention on this Tuya model: percent_state = 0 → fully open,
  // 100 → fully closed. Present it as an "openness %" for the human, i.e.
  // 100 - pos so bigger numbers = more open.
  const openness = 100 - pos;
  const moving = cover.work_state === "opening" || cover.work_state === "closing";
  const label = cover.work_state === "opening" ? "In apertura…" :
                cover.work_state === "closing" ? "In chiusura…" :
                openness === 100 ? "Aperta" :
                openness === 0 ? "Chiusa" :
                `Aperta al ${openness}%`;

  return (
    <div className="rounded-xl border border-[#e8dcc8] bg-[#faf3ee] p-3" data-testid={`cover-${cover.device_id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="font-semibold text-[#2b1410] truncate">{cover.name || "Tapparella"}</div>
          <div className="text-[11px] text-[#7c5a4e] flex items-center gap-1.5 mt-0.5">
            {cover.room && <span className="uppercase tracking-wide">{cover.room}</span>}
            {cover.online
              ? <span className="inline-flex items-center gap-0.5 text-emerald-700"><Wifi className="h-3 w-3"/> online</span>
              : <span className="inline-flex items-center gap-0.5 text-red-600"><WifiOff className="h-3 w-3"/> offline</span>}
            {cover.fault > 0 && (
              <span className="inline-flex items-center gap-0.5 text-amber-700"><AlertTriangle className="h-3 w-3"/> guasto</span>
            )}
          </div>
        </div>
        <div className={`text-[11px] px-2 py-0.5 rounded-full ${moving ? "bg-amber-100 text-amber-800 animate-pulse" : "bg-emerald-100 text-emerald-800"}`}>
          {label}
        </div>
      </div>

      {/* Open-progress bar */}
      <div className="h-2 rounded-full bg-[#e8dcc8] overflow-hidden mb-3">
        <div
          className="h-full bg-[#4a1c17] transition-all duration-500"
          style={{ width: `${openness}%` }}
        />
      </div>

      {/* Main action buttons */}
      <div className="grid grid-cols-3 gap-2">
        <button
          disabled={busy || !cover.online}
          onClick={() => onCommand("open")}
          data-testid={`cover-open-${cover.device_id}`}
          className="inline-flex flex-col items-center gap-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-neutral-200 disabled:text-neutral-500 text-white px-2 py-2 text-xs font-semibold"
        >
          <ChevronsUp className="h-4 w-4" /> Apri
        </button>
        <button
          disabled={busy || !cover.online}
          onClick={() => onCommand("stop")}
          data-testid={`cover-stop-${cover.device_id}`}
          className="inline-flex flex-col items-center gap-1 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:bg-neutral-200 disabled:text-neutral-500 text-white px-2 py-2 text-xs font-semibold"
        >
          <Square className="h-4 w-4" /> Ferma
        </button>
        <button
          disabled={busy || !cover.online}
          onClick={() => onCommand("close")}
          data-testid={`cover-close-${cover.device_id}`}
          className="inline-flex flex-col items-center gap-1 rounded-lg bg-[#4a1c17] hover:bg-[#2b1410] disabled:bg-neutral-200 disabled:text-neutral-500 text-white px-2 py-2 text-xs font-semibold"
        >
          <ChevronsDown className="h-4 w-4" /> Chiudi
        </button>
      </div>

      {/* Position presets */}
      <div className="flex flex-wrap gap-1 mt-2">
        <span className="text-[10px] uppercase tracking-wide text-[#7c5a4e] self-center mr-1">Preset:</span>
        {[25, 50, 75].map((openPct) => (
          <button
            key={openPct}
            disabled={busy || !cover.online}
            onClick={() => onPosition(100 - openPct)}
            data-testid={`cover-preset-${cover.device_id}-${openPct}`}
            className="text-[11px] px-2 py-0.5 rounded-full border border-[#e8dcc8] bg-white hover:bg-[#f7ebdf] disabled:opacity-40 disabled:cursor-not-allowed text-[#4a1c17]"
          >
            {openPct}%
          </button>
        ))}
      </div>
    </div>
  );
}
